// src/components/auth/ForgotPassword.tsx
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkInput from '../common/SparkInput';
import SparkHeading from '../common/SparkHeading';
import SparkFooter from '../common/SparkFooter';

const ForgotPassword: React.FC = () => {
  const { resetPassword, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    try {
      await resetPassword(email);
      setMessage('Password reset email sent! Check your inbox and follow the instructions to reset your password.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please check your email address.');
    }
  };

  if (loading) {
    return (
      <SparkLayout>
        <SparkCard>
          <div className="loading-container flex items-center justify-center h-40">
            <div className="text-center">
              <div className="spark-spinner mx-auto mb-4" />
              <p className="text-xl">Loading...</p>
            </div>
          </div>
        </SparkCard>
      </SparkLayout>
    );
  }

  return (
    <SparkLayout>
      <SparkCard className="max-w-md mx-auto">
        <SparkHeading>Reset Password</SparkHeading>
        
        <div className="mb-6 text-center text-white/90">
          <p>Enter your email address and we'll send you a link to reset your password.</p>
        </div>

        {error && (
          <div className="spark-message spark-message--error mb-6">
            {error}
          </div>
        )}

        {message && (
          <div className="spark-message spark-message--success mb-6">
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <SparkInput
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
          />

          <SparkFooter>
            <SparkButton 
              type="submit" 
              fullWidth
              disabled={loading || !email.trim()}
            >
              {loading ? 'SENDING...' : 'SEND RESET EMAIL'}
            </SparkButton>
          </SparkFooter>
        </form>

        <div className="mt-8 text-center space-y-4">
          <div className="text-white/80">
            Remember your password?{' '}
            <SparkButton 
              variant="secondary" 
              to="/login"
              className="text-sm inline-block ml-2"
            >
              BACK TO LOGIN
            </SparkButton>
          </div>
          
          <div className="text-white/80">
            Don't have an account?{' '}
            <SparkButton 
              variant="secondary" 
              to="/register"
              className="text-sm inline-block ml-2"
            >
              SIGN UP
            </SparkButton>
          </div>
        </div>
      </SparkCard>
    </SparkLayout>
  );
};

export default ForgotPassword;